import React, { useState, useEffect } from 'react';
import { Paper, Typography, Button, Alert, Box, CircularProgress, Stack } from '@mui/material';
import { Edit } from '@mui/icons-material';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import MarkdownEditor from '../Common/MarkdownEditor';
import axios from '../../api/axios';
import { useNotification } from '../../utils/useNotification';

function ShortAnswerComponent({ example, onAnswerSubmitted, isTeacher = false, embedded = false }) {
  const [answer, setAnswer] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const { showPointsNotification, NotificationComponent } = useNotification();
  
  // 获取已提交的答案（复习/嵌入模式下不预填，由 AI 解答）
  useEffect(() => {
    if (embedded) return;
    const fetchSubmittedAnswer = async () => {
      try {
        const questionId = `notebook_${example.id}`;
        const response = await axios.get(`/submissions/question/${questionId}`);
        const submissions = response.data || [];
        if (submissions.length > 0) {
          const latestSubmission = submissions[0];
          try {
            const answerData = JSON.parse(latestSubmission.code);
            if (answerData.answer) {
              setAnswer(answerData.answer);
              setHasSubmitted(true);
              setSubmissionResult({
                status: latestSubmission.status,
                isCorrect: latestSubmission.status === 'passed',
                score: latestSubmission.score
              });
            }
          } catch (e) {
            // 如果解析失败，尝试直接使用
            if (latestSubmission.code) {
              setAnswer(latestSubmission.code);
              setHasSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching submitted answer:', error);
      }
    };
    
    if (example.id) {
      fetchSubmittedAnswer();
    }
  }, [example.id, embedded]);
  
  const handleSubmit = async () => {
    if (!answer.trim()) {
      alert('请输入答案');
      return;
    }
    
    setSubmitting(true);
    setSubmissionResult(null);
    
    try {
      const response = await axios.post(`/notebook-examples/${example.id}/submit`, {
        answer: answer.trim()
      });
      
      const submission = response.data.submission;
      if (embedded) {
        setAnswer('');
        setHasSubmitted(false);
        setSubmissionResult(null);
        setShowAnswer(false);
        setIsEditing(false);
      } else {
        setSubmissionResult(submission);
        setHasSubmitted(true);
        setIsEditing(false);
        if (!example.enableAI && submission.status === 'passed') {
          setShowAnswer(true);
        }
      }
      
      if (!embedded && submission.status === 'passed' && submission.score > 0 && !isTeacher) {
        showPointsNotification(submission.score);
      }
      
      if (onAnswerSubmitted) {
        if (embedded) {
          onAnswerSubmitted(answer.trim(), { isCorrect: submission.status === 'passed', score: submission.score });
        } else {
          onAnswerSubmitted(answer.trim());
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setSubmissionResult({
        status: 'error',
        isCorrect: false,
        error: error.response?.data?.error || '提交失败，请重试'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // 复习/嵌入模式：只显示题目，答案在页面下方输入框提交
  if (embedded) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <MarkdownRenderer content={example.question} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          请在页面右边输入框输入你的答案并发送。
        </Typography>
        <NotificationComponent />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 2, sm: 2, md: 2 },
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* 与代码题一致的左右分栏布局：各占 50% 宽、填满竖直空间 */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', flexDirection: 'row', minHeight: 0 }}>
        {/* 左侧：题目区 */}
        <Box sx={{ 
          width: '50%', 
          flex: '0 0 50%', 
          minWidth: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          borderRight: 1,
          borderColor: 'divider',
          pr: 2
        }}>
          <Paper
            elevation={embedded ? 0 : undefined}
            sx={{ 
              p: 2, 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              position: 'relative',
              boxSizing: 'border-box',
              border: 1,
              borderColor: 'divider',
              ...(embedded && { bgcolor: 'transparent' }),
            }}
          >
            <Box sx={{ 
              flex: '1 1 0',
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 0,
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                },
              },
            }}>
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={example.question} />
              </Box>
              
              {hasSubmitted && !isEditing && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box component="div">
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      <strong>已提交的答案：</strong>
                    </Typography>
                    <Box component="div" sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <MarkdownRenderer content={answer} />
                    </Box>
                  </Box>
                </Alert>
              )}
              
              {!embedded && submissionResult && (
                <Alert 
                  severity={submissionResult.status === 'pending' ? 'info' : submissionResult.isCorrect ? 'success' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  <Box component="div">
                    <Typography variant="body2" component="div" sx={{ mb: submissionResult.feedback ? 1 : 0 }}>
                      {submissionResult.aiScore !== undefined ? (
                        <>
                          <strong>得分：{submissionResult.aiScore}/10</strong>
                          {submissionResult.isCorrect ? ' ✓ 回答正确！' : ' ✗ 回答不正确'}
                        </>
                      ) : submissionResult.status === 'pending' ? (
                        '✓ 答案已提交，等待评分'
                      ) : submissionResult.isCorrect ? (
                        <>
                          <strong>得分：{submissionResult.score || 0}/100</strong>
                          {' ✓ 回答正确！'}
                        </>
                      ) : (
                        '✗ 回答错误'
                      )}
                      {submissionResult.error && ` - ${submissionResult.error}`}
                    </Typography>
                    {submissionResult.feedback && (
                      <Box sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" component="div">
                          <strong>AI评语：</strong>
                          <MarkdownRenderer content={submissionResult.feedback} />
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}
            </Box>
            
            <Box sx={{ 
              flexShrink: 0,
              pt: 2,
              pb: 2,
              borderTop: 1, 
              borderColor: 'divider',
            }}>
              <Stack direction="row" spacing={2}>
                {(!hasSubmitted || isEditing || embedded) ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!answer.trim() || submitting}
                    sx={{ minWidth: 120 }}
                  >
                    {submitting ? <CircularProgress size={20} /> : hasSubmitted ? '重新提交' : '提交答案'}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => {
                        setIsEditing(true);
                        setShowAnswer(false);
                      }}
                    >
                      修改答案
                    </Button>
                    <Button
                      variant={showAnswer ? 'outlined' : 'contained'}
                      onClick={() => setShowAnswer(!showAnswer)}
                      disabled={!isTeacher && !hasSubmitted && !isEditing}
                    >
                      {showAnswer ? '隐藏答案' : '显示答案'}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          </Paper>
        </Box>
        
        {/* 右侧：答案区 */}
        <Box sx={{ 
          width: '50%', 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          minWidth: 0,
          pl: 2
        }}>
          <Paper
            elevation={embedded ? 0 : undefined}
            sx={{ 
              p: 2, 
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: 1,
              borderColor: 'divider',
              ...(embedded && { bgcolor: 'transparent' }),
            }}
          >
            {(!showAnswer || isEditing) && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <MarkdownEditor
                  value={answer}
                  onChange={(value) => setAnswer(value)}
                  label="答案"
                  minRows={12}
                  maxRows={25}
                  showPreview={true}
                  showToolbar={true}
                  placeholder="请输入答案...（支持Markdown格式）"
                  disabled={submitting}
                  disableImageUpload={true}
                  fillHeight={true}
                />
              </Box>
            )}
            
            {!embedded && showAnswer && example.answer && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box component="div" variant="body2">
                  <strong>参考答案：</strong>
                  <MarkdownRenderer content={example.answer} />
                </Box>
              </Alert>
            )}
            
            {!embedded && showAnswer && example.explanation && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box component="div" variant="body2">
                  <strong>解释：</strong>
                  <MarkdownRenderer content={example.explanation} />
                </Box>
              </Alert>
            )}
          </Paper>
        </Box>
      </Box>
      
      <NotificationComponent />
    </Box>
  );
}

export default ShortAnswerComponent;
